import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import "./TrainingHistory.css";
import dropdownIcon from "../../Assets/arrow-down.svg";
import { io } from "socket.io-client";
import { FaStar } from "react-icons/fa";
import { FaTrash } from "react-icons/fa";
import { createPortal } from "react-dom";
import useBackSwipe from "../../hooks/useBackSwipe";
import { animateBackAndRun } from "../../hooks/useBackSwipe";
import { lightHaptic } from "../../native/capacitor";
import {
  enqueueFavoriteToggle,
  getCachedAnalysisDetails,
  getCachedSessions,
  setCachedAnalysisDetails,
  setCachedSessions,
} from "../../offline/sessionOffline";

const TrainingHistory = ({
  userId,
  isMobile,
  isOnline = true,
  onBack,
  showAnalysisView,
  setShowAnalysisView,
  onRestore,
}) => {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [analysisDetails, setAnalysisDetails] = useState([]);
  const [expandedDates, setExpandedDates] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sessionIdToDelete, setSessionIdToDelete] = useState(null);
  const mobileAnalysisPanelRef = useRef(null);
  const mobileListUnderlayRef = useRef(null);
  const mobileListDimRef = useRef(null);

  useBackSwipe(
    () => setShowAnalysisView(false),
    {
      enabled: isMobile && showAnalysisView,
      target: mobileAnalysisPanelRef,
      underlay: mobileListUnderlayRef,
      dimmer: mobileListDimRef,
    }
  );

  useEffect(() => {
    if (!showDeleteModal) return undefined;
    const prevBodyOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [showDeleteModal]);

  const confirmDeleteSession = (sessionId) => {
    setSessionIdToDelete(sessionId);
    setShowDeleteModal(true);
  };

  const toggleFavorite = async (sessionId, currentVal) => {
    lightHaptic();
    setSessions((prev) =>
      prev.map((s) =>
        s.id === sessionId ? { ...s, is_favorite: !currentVal } : s
      )
    );

    if (!isOnline) {
      enqueueFavoriteToggle({ sessionId, userId });
      return;
    }

    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/toggle-favorite`,
        { sessionId, userId }
      );
      setSessions((prev) =>
        prev.map((s) =>
          s.id === sessionId ? { ...s, is_favorite: data.isFavorite } : s
        )
      );
    } catch (err) {
      console.error("toggle favorite error:", err);
      enqueueFavoriteToggle({ sessionId, userId });
    }
  };

  const executeDeleteSession = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/sessions/${sessionIdToDelete}`
      );
      setSessions((prev) => prev.filter((s) => s.id !== sessionIdToDelete));
      if (sessionIdToDelete === selectedSessionId) {
        setSelectedSessionId(null);
        setAnalysisDetails([]);
      }
    } catch (err) {
      console.error("Error deleting session:", err);
      alert("Failed to delete session. Please try again.");
    } finally {
      setShowDeleteModal(false);
      setSessionIdToDelete(null);
    }
  };

  // Fetch user sessions initially
  useEffect(() => {
    if (!userId) return;
    const cached = getCachedSessions(userId);
    if (cached?.sessions?.length) {
      setSessions(cached.sessions);
      setIsLoading(false);
    }

    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/${userId}`)
      .then((res) => {
        const { sessions } = res.data;
        setSessions(sessions);
        setCachedSessions(userId, sessions);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching sessions", err);
        setIsLoading(false);
      });
  }, [userId]);

  useEffect(() => {
    if (!userId || !sessions.length) return;
    setCachedSessions(userId, sessions);
  }, [userId, sessions]);

  // Render the final summary from the DB
  const renderAnalysisSummary = () => {
    if (!selectedSessionId) {
      return (
        <div className="analysis-summary">
          <div className="title-with-dot">
            <span className="orange-dot" />
            <h2>Session Summary</h2>
          </div>
          <div className="orange-divider" />
          <div className="empty-message">
            <p>Please select a session to view its analysis summary.</p>
          </div>
        </div>
      );
    }

    // If we do have a session
    return (
      <div className="analysis-summary">
        <div className="title-with-dot">
          <span className="orange-dot" />
          <h2>Session Summary</h2>
        </div>
        <div className="orange-divider" />

        <button
          className="restore-session-btn"
          onClick={() => {
            const sessionToRestore = sessions.find(
              (s) => s.id === selectedSessionId
            );
            if (sessionToRestore) {
              onRestore(sessionToRestore);
            }
          }}
        >
          Restore This Session
        </button>

        {typeof analysisDetails?.summary !== "object" ||
        analysisDetails.summary === null ? (
          <div className="empty-message">
            <p>⚠️ No analysis summary found for this session.</p>
          </div>
        ) : (
          <div className="structured-summary">
            <h4 className="summary-section-title">AI Feedback</h4>
            {typeof analysisDetails.summary.feedback === "object" &&
              analysisDetails.summary.feedback !== null ? (
                <div className="structured-feedback">
                  <p className="summary-paragraph">
                    <strong>Summary:</strong>{" "}
                    {analysisDetails.summary.feedback.positive_summary}
                  </p>

                  <h4 className="summary-subtitle">Areas to Improve:</h4>
                  <ul className="summary-bullet-list">
                    {Object.entries(analysisDetails.summary.feedback.negative_feedback).map(
                      ([key, val]) => (
                        <li key={key} className="summary-paragraph">
                          <strong>
                            {key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                          </strong>
                          <br />
                          {val.summary}
                        </li>
                      )
                    )}
                  </ul>
                </div>
              ) : (
              <p className="summary-paragraph">No feedback available.</p>
            )}

            <h4 className="summary-section-title">Self-Reflection Q&A</h4>
            {Array.isArray(analysisDetails.summary.chatTranscript) ? (
              <ul className="summary-bullet-list">
                {analysisDetails.summary.chatTranscript.map((entry, idx) => (
                  <li key={idx}>
                    <strong>{entry.role === "assistant" ? "Q" : "A"}:</strong>{" "}
                    {entry.message}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="summary-paragraph">
                No self-reflection data available.
              </p>
            )}

            <h4 className="summary-section-title">Reflection Summary</h4>
            <p className="summary-paragraph">
              {typeof analysisDetails.summary.chatSummary === "string"
                ? analysisDetails.summary.chatSummary
                : "No summary available."}
            </p>
          </div>
        )}
      </div>
    );
  };

  // Called when user clicks a session
  const handleSessionClick = (sessionId) => {
    setSelectedSessionId(sessionId);
    const cachedDetails = getCachedAnalysisDetails(sessionId);
    if (cachedDetails?.details) {
      setAnalysisDetails(cachedDetails.details);
      if (isMobile) {
        setShowAnalysisView(true);
      }
    }

    if (!isOnline) return;

    axios
      .get(
        `${process.env.REACT_APP_API_BASE_URL}/api/analysis-details/${sessionId}`
      )
      .then((res) => {
        let data = res.data;
        // Possibly parse the stored summary if it's a string
        if (typeof data.summary === "string") {
          try {
            data.summary = JSON.parse(data.summary);
          } catch (err) {
            console.error("Failed to parse summary JSON:", err);
          }
        }
        setAnalysisDetails(data);
        setCachedAnalysisDetails(sessionId, data);
        if (isMobile) {
          setShowAnalysisView(true);
        }
      })
      .catch((err) => console.error("Error fetching analysis", err));
  };

  // Listen for analysis completion in real-time
  useEffect(() => {
    if (!userId || !isOnline) return;

    const socket = io(process.env.REACT_APP_API_BASE_URL);

    // Refresh sessions
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/${userId}`)
      .then((res) => {
        const { sessions } = res.data;
        setSessions(sessions);
      })
      .catch((err) => console.error("Error fetching sessions", err));

    socket.on("analysis_complete", (data) => {
      if (data.userId === userId) {
        // Refresh session list
        axios
          .get(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/${userId}`)
          .then((res) => {
            const { sessions } = res.data;
            setSessions(sessions);
            setCachedSessions(userId, sessions);
          })
          .catch((err) => console.error("Error refreshing sessions", err));

        // If user is viewing the session, also refresh analysis
        if (data.videoId === selectedSessionId) {
          axios
            .get(
              `${process.env.REACT_APP_API_BASE_URL}/api/analysis-details/${data.videoId}`
            )
            .then((res) => {
              let refreshedData = res.data;
              if (typeof refreshedData.summary === "string") {
                try {
                  refreshedData.summary = JSON.parse(refreshedData.summary);
                } catch (e) {
                  console.error("Error parsing refreshed summary:", e);
                }
              }
              setAnalysisDetails(refreshedData);
              setCachedAnalysisDetails(data.videoId, refreshedData);
            })
            .catch((err) => console.error("Error refreshing analysis", err));
        }
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [isOnline, userId, selectedSessionId]);

  // Toggle a date group open/closed
  const toggleDateGroup = (date) => {
    setExpandedDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  // Group sessions by EST date
  const groupedByDate = sessions
    .filter((s) => s.status !== "processing" && s.feedback_provided)
    .reduce((acc, session) => {
      const dateObj = new Date(session.created_at);
      const day = dateObj.toLocaleDateString("en-US", {
        timeZone: "America/New_York",
        weekday: "long",
      });
      const formattedDate = dateObj.toLocaleDateString("en-US", {
        timeZone: "America/New_York",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      const displayDate = `${day} – ${formattedDate}`;
      if (!acc[displayDate]) acc[displayDate] = [];
      acc[displayDate].push(session);
      return acc;
    }, {});

  // Helper to format only the EST time
  const formatDateTime = (isoString) => {
    const options = {
      timeZone: "America/New_York",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    };
    return new Date(isoString).toLocaleTimeString("en-US", options) + " EST";
  };

    if (!userId) {
    return (
      <div className="training-container loading-state">
        <div className="loading-message">
          <p>👤 This is a guest session. Session history is not available.</p>
        </div>
      </div>
    );
  }

  // Render
  if (isLoading) {
    return (
      <div className="training-container loading-state">
        <div className="loading-message">
          <div className="spinner" />
          <p>Loading your session summaries...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`training-container fade-in-training ${showAnalysisView ? "analysis-open" : ""}`}>
      <div className="training-sidebar" ref={mobileListUnderlayRef}>
          <h2>Your Session Summary</h2>
          {Object.keys(groupedByDate).length === 0 ? (
            <div className="empty-message">
              <p>📭 You haven’t uploaded any sessions yet.</p>
              <p>Once you upload a video, it’ll show up here for review.</p>
            </div>
          ) : (
            <ul className="date-group-list">
              {Object.keys(groupedByDate).map((date) => (
                <li key={date} className="date-group">
                  <div
                    className="date-toggle"
                    onClick={() => toggleDateGroup(date)}
                  >
                    <img
                      src={dropdownIcon}
                      alt="toggle"
                      className={`dropdown-arrow ${
                        expandedDates[date] ? "" : "rotated"
                      }`}
                    />
                    {date}
                  </div>
                  <div
                    className={`session-collapse ${
                      expandedDates[date] ? "expanded" : ""
                    }`}
                  >
                    {expandedDates[date] && (
                      <ul className="session-list">
                        {groupedByDate[date].map((s, index) => (
                          <li key={s.id}>
                            <button
                              className={`session-button ${
                                s.id === selectedSessionId ? "active" : ""
                              }`}
                              onClick={() => handleSessionClick(s.id)}
                            >
                              <div className="session-row">
                                <div className="session-left">
                                  <div className="session-info-row">
                                    <span className="session-number">
                                      {index + 1}.
                                    </span>
                                    <span className="session-type">
                                      {s.is_demo ? "Demo Video" : "User Upload"}
                                    </span>
                                  </div>
                                  <span className="session-time">
                                    {formatDateTime(s.created_at)}
                                  </span>
                                </div>

                                <div className="session-actions">
                                  <button
                                    className="icon-button favorite"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleFavorite(s.id, s.is_favorite);
                                    }}
                                  >
                                    <FaStar
                                      size={20}
                                      color={s.is_favorite ? "#f5b301" : "#bbb"}
                                      style={{
                                        fill: s.is_favorite
                                          ? "#f5b301"
                                          : "#ccc",
                                      }}
                                    />
                                  </button>

                                  <button
                                    className="icon-button delete"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      confirmDeleteSession(s.id);
                                    }}
                                    title="Delete session"
                                  >
                                    <FaTrash
                                      size={16}
                                      style={{ fill: "#aaa" }}
                                    />
                                  </button>
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      {isMobile && showAnalysisView && (
        <div className="training-underlay-dim" ref={mobileListDimRef} />
      )}

      {isMobile && showAnalysisView ? (
        <div
          className="analysis-panel mobile-analysis-panel-enter mobile-analysis-panel-overlay"
          ref={mobileAnalysisPanelRef}
        >
          <button
            className="back-button-training"
            onClick={() =>
              animateBackAndRun({
                target: mobileAnalysisPanelRef,
                underlay: mobileListUnderlayRef,
                dimmer: mobileListDimRef,
                onBack: () => setShowAnalysisView(false),
              })
            }
          >
            ← Back
          </button>
          {renderAnalysisSummary()}
        </div>
      ) : (
        // Desktop
        !isMobile && (
          <div className="analysis-panel">{renderAnalysisSummary()}</div>
        )
      )}

      {showDeleteModal &&
        createPortal(
          <div
            className="modal-overlay-sessions"
            onClick={() => setShowDeleteModal(false)}
          >
            <div
              className="modal-content-sessions"
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Confirm Delete</h3>
              <p>
                Are you sure you want to delete this session? This action
                cannot be undone.
              </p>
              <div className="modal-actions-sessions">
                <button
                  className="cancel-delete"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="confirm-delete"
                  onClick={executeDeleteSession}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TrainingHistory;
